import { defineStore } from "pinia";
import IDeviceProperty from "../models/Devices/deviceProperty";
import IDevicePropertyForm from "../models/forms/devicePropertyForm";
import DevicePropertyServices from "../services/DevicePropertyService";
import IDevicePropertyFilter from "../models/filters/devicePropertyFilter";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import  DataOperationError  from "../models/api/dataOperationError";
import { IDevice } from "../models/Devices/device";
import { useStartupStore } from "./startup.store";

export const useDevicePropertyStore = defineStore("devicePropertys",{
    state: () => ({
        currentDeviceId: 0 as number,
        datas: [] as Array<IDeviceProperty>,
        current : {} as IDeviceProperty,
        itemForm : {} as IDevicePropertyForm
    }),
    getters: {
        /**
         * Récupère un deviceProperty par son identifiant dans le store, sans passer par l'api
         * @param state 
         * @returns 
         */
        getDevicePropertyById: (state) => (id: number):IDeviceProperty|undefined => {
            return state.datas?.find((deviceProperty) => deviceProperty.id == id);
        },

        /**
         * Récupére une propriété en particulier
         */
        getDevicePropertyByKey: (state) => (idDevice: number, key: string):IDeviceProperty|undefined => {
            return state.datas?.find((deviceProperty) => deviceProperty.device.id == idDevice && deviceProperty.key == key)
        },

        getCurrentDeviceProperties(state) {
            return state.datas.filter(dp => dp.device.id == state.currentDeviceId);
        }
    },
    actions: {
        /**
         * Chargement des données dans le store
         * @param filter Le filtre à appliquer pour le chargement des données
         * @returns 
         */
        async load(filter: IDevicePropertyFilter) {
            const startupStore = useStartupStore();
            if(startupStore.isMainInitializing) {
                let result = await DevicePropertyServices.getAll<IDevicePropertyFilter,IDeviceProperty>(filter);
                this.datas = [];
                if (!result.error) {
                    this.datas = result.data as IDeviceProperty[];
                }
                return result;
            }
            else {
                console.error("Store loading aborted : triggered from outside main.ts context");
            }
        },


        /**
         * Récupération d'un deviceProperty par son identifiant
         * @param id 
         * @returns 
         */
        async get(id: number) {
            let result = await DevicePropertyServices.getById<IDeviceProperty>(id);
            // mettre à jour dans le store ?
            if ( !result.error) {
                let index = this.datas.findIndex((a) => a.id == id);
                if (index >= 0)
                    this.datas[index] = result.data as IDeviceProperty;
                // on le stock dans le current puis dans le form
                this.current = result.data as IDeviceProperty;
                this.itemForm = this.toForm(result.data as IDeviceProperty);
            }
            return result;
        },

        /**
         * Récupération de l'objet dans le store
         * @param id Identifiant de l'objet
         * @returns 
         */
        async searchStoreById (id: number):Promise<IDeviceProperty> {
            this.current = this.datas.find((objet) => objet.id == id)!;
            this.itemForm = await this.toForm(this.current as IDeviceProperty);
            return this.current;
        },

        async save(maj:boolean = true, isPatch:boolean = false)
        {
            let result = {} as ServiceOperationResult<IDeviceProperty|IDataOperationResult<IDeviceProperty>>;  
            if ( this.itemForm.id > 0) {
                result = await DevicePropertyServices.update(this.itemForm);
                if ( !result.error)
                {
                    this.current = result.data as IDeviceProperty;
                    if ( maj) {
                        let index = this.datas.findIndex((a) => a.id == this.itemForm.id);
                        if (index >= 0) {
                            this.datas[index] = this.current;
                        } else {
                            const error = new DataOperationError();
                            error.REQUEST_ERROR = "Item non trouvé dans le store";
                            return new ServiceOperationResult<IDataOperationResult<IDeviceProperty>>(true, {errors: error});
                        }
                    }
                }
            } else {
                result = await DevicePropertyServices.create(this.itemForm);
                if ( !result.error)
                {
                    this.current = result.data as IDeviceProperty;
                    this.datas.push(this.current);
                }
            }
            return result;
        },

        toForm(deviceProperty: IDeviceProperty):IDevicePropertyForm {
            const form = {} as IDevicePropertyForm;
            Object.assign(form, deviceProperty);
            form.deviceId = deviceProperty.device.id;
            return form;
        },
        // permet d'initialiser un objet vide pour le formulaire
        newForm(lienId:number):IDevicePropertyForm {
            this.itemForm = {
                deviceId: lienId,
                id: 0,
                key: "",
                value:"",
                fieldType:0
            } as IDevicePropertyForm;
            return this.itemForm;
        },
        // permet d'initialiser un objet vide pour les referentiels
        newItem(lien:IDevice):IDeviceProperty {
            this.current = { 
                id: 0,
                device : lien,
                key: "",
                value:"",
                fieldType:0,
                addingDate: new Date(),
                active: true
            } as IDeviceProperty;
            return this.current;
        }
    }
});